import { useEffect, useState, Fragment } from "react";
import Cookies from "js-cookie";
import Loading from "../../components/utils/Loading";
import DocumentRow from "./DocumentRow";
import Pagination from "../../components/utils/Pagination";
import DocumentHeader from "./DocumentHeader";
import { useTranslation } from "react-i18next";
import {
  getCorporationData,
  updateCorporationDocuments,
} from "../../services/corporation";
import {
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import EmptyState from "../../components/EmptyState";
import PageHeader from "../../components/PageHeader";
import StandardTable from "../../ui/components/StandardTable";
import { toast } from "react-toastify";

const DOC_TYPES = [
  { id: "facture", labelKey: "Invoice" },
  { id: "etat-des-lieux", labelKey: "Assessment" },
  { id: "autres", labelKey: "Other" },
];

const Document = () => {
  const { t } = useTranslation();
  const { token, ...user } = JSON.parse(localStorage.getItem("user"));
  const viewMode = Cookies.get("viewMode") || "client";
  const isAkanthasAccount = (user?.email || "").includes("@akanthas.com");
  const canAddDocuments = isAkanthasAccount && viewMode === "admin";

  const [documents, setDocuments] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [sortByField, setSortByField] = useState("name");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [filter, setFilter] = useState("facture");
  const [keywordFilter, setKeywordFilter] = useState("");

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);
  const [newDoc, setNewDoc] = useState({
    type: "autres",
    name: "",
    url: "",
    date: new Date().toISOString().slice(0, 10),
    extension: "pdf",
    sharedWithClient: true,
  });

  useEffect(
    () => {
      const fetchData = async () => {
        await getCorporationData(user).then((responseCorporation) => {
          if (
            responseCorporation.status >= 200 &&
            responseCorporation.status < 400
          ) {
            const allDocs =
              responseCorporation?.data?.corporation?.documents || [];
            const filteredDocs = allDocs.filter((doc) => doc.type === filter);
            setDocuments(filteredDocs);
            setFullData(allDocs);
          }
        });
        setIsLoading(false);
      };
      fetchData();
    },
    // eslint-disable-next-line
    [],
  );

  useEffect(
    () => {
      sortData(sortByField, orderSortByField);
    },
    // eslint-disable-next-line
    [sortByField, orderSortByField],
  );

  useEffect(() => {
    if (!Array.isArray(fullData)) return;

    let filteredDocs;
    if (filter === "autres") {
      const knownTypes = ["facture", "etat-des-lieux"];
      filteredDocs = fullData.filter((doc) => !knownTypes.includes(doc.type));
    } else {
      filteredDocs = fullData.filter((doc) => doc.type === filter);
    }

    setDocuments((prev) => {
      const sameLength = prev.length === filteredDocs.length;
      const sameIds =
        sameLength && prev.every((doc, i) => doc._id === filteredDocs[i]?._id);
      return sameIds ? prev : filteredDocs;
    });
  }, [fullData, filter]);

  const sortData = (sortBy, order) => {
    const sorted = [...documents].sort((a, b) => {
      const aValue = a[sortBy] ? a[sortBy].toString().toLowerCase() : "";
      const bValue = b[sortBy] ? b[sortBy].toString().toLowerCase() : "";

      if (aValue < bValue) return order === "1" ? -1 : 1;
      if (aValue > bValue) return order === "1" ? 1 : -1;
      return 0;
    });

    setDocuments(sorted);
  };

  const sharedDocuments = (documents || []).filter(
    (doc) => doc.sharedWithClient,
  );

  const filteredDocuments = sharedDocuments.filter((doc) => {
    if (!keywordFilter) return true;
    const searchTerm = keywordFilter.toLowerCase();
    return (doc?.name || "").toLowerCase().includes(searchTerm);
  });

  useEffect(() => {
    setResultPage(Math.ceil(filteredDocuments.length / recordPerPage));
    setCurrentPage(1);
  }, [filter, keywordFilter, filteredDocuments.length, recordPerPage]);

  const startIndex = (currentPage - 1) * recordPerPage;
  const endIndex = startIndex + recordPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);
  const hasDocuments = paginatedDocuments.length > 0;
  const hasAnyDocuments = sharedDocuments.length > 0;
  const hasActiveFilters = Boolean(keywordFilter?.trim());
  const showEmptyState = !hasAnyDocuments && !hasActiveFilters;

  const handleAddDocument = async () => {
    if (!newDoc.name?.trim() || !newDoc.url?.trim()) {
      toast.error(t("Please fill in name and URL"));
      return;
    }
    setSavingDocument(true);
    try {
      const nextDocs = [
        ...fullData,
        {
          ...newDoc,
          date: newDoc.date ? new Date(newDoc.date).toISOString() : undefined,
        },
      ];
      const response = await updateCorporationDocuments(nextDocs);
      if (response?.status >= 200 && response?.status < 400) {
        const updated = response?.data?.corporation?.documents ?? nextDocs;
        setFullData(updated);
        setAddModalOpen(false);
        setNewDoc({
          type: "autres",
          name: "",
          url: "",
          date: new Date().toISOString().slice(0, 10),
          extension: "pdf",
          sharedWithClient: true,
        });
        toast.success(t("File uploaded successfully"));
      } else {
        toast.error(response?.data?.error || t("Failed to upload file"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || t("Failed to upload file"));
    } finally {
      setSavingDocument(false);
    }
  };

  if (token) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader
          title={t("My Akanthas Documents")}
          tabs={[
            { id: "facture", label: t("Invoice") },
            { id: "etat-des-lieux", label: t("Assessment") },
            { id: "autres", label: `${t("Other")} ${t("documents")}` },
          ]}
          activeTab={filter}
          onTabChange={(tabId) => setFilter(tabId)}
        />
        <div className="settings-content-wrapper">
          {isLoading ? (
            <Loading color="#F5F5F5" />
          ) : (
            <div className="settings-list-shell">
              <Card className="table border-0 shadow-none bg-white rounded-lg">
                <StandardTable
                  fluidColCount={showEmptyState ? 1 : 2}
                  fixedColWidth={showEmptyState ? 0 : 100}
                  containerClassName={
                    showEmptyState
                      ? "table-standard-container--list-empty"
                      : undefined
                  }
                  columnAlignments={
                    showEmptyState ? ["left"] : ["left", "left", "right"]
                  }
                  topBar={
                    hasAnyDocuments ? (
                      <section className="table-standard-top-bar">
                        <div className="table-standard-top-bar-inner">
                          <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                            <div className="search-input-wrapper">
                              <i className="fas fa-search search-input-icon" />
                              <input
                                type="text"
                                className="search-input"
                                value={keywordFilter}
                                placeholder={`${t("Search")} (${t("Name")})`}
                                onChange={(event) =>
                                  setKeywordFilter(event.target.value)
                                }
                              />
                            </div>
                          </div>
                          {canAddDocuments ? (
                            <div className="create-button-wrapper">
                              <button
                                type="button"
                                className="create-button"
                                onClick={() => setAddModalOpen(true)}
                              >
                                {t("Add a document")}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </section>
                    ) : null
                  }
                  header={
                    hasAnyDocuments ? (
                      <DocumentHeader
                        onSort={(field, order) => {
                          setSortByField(field);
                          setOrderSortByField(order);
                        }}
                        sortByField={sortByField}
                        orderSortByField={orderSortByField}
                      />
                    ) : null
                  }
                  bottom={
                    hasAnyDocuments ? (
                      <div className="table-standard-bottom table-pagination-bottom">
                        <Pagination
                          totalEntry={filteredDocuments.length}
                          recordPerPage={recordPerPage}
                          setRecordPerPage={setRecordPerPage}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          resultPage={resultPage}
                          setResultPage={setResultPage}
                        />
                      </div>
                    ) : null
                  }
                >
                  {hasDocuments ? (
                    paginatedDocuments.map((doc) => (
                      <Fragment key={doc._id}>
                        <DocumentRow data={doc} />
                      </Fragment>
                    ))
                  ) : hasAnyDocuments && hasActiveFilters ? (
                    <div className="table-standard-row table-standard-row--empty">
                      <div
                        className="table-standard-row-cell"
                        style={{ gridColumn: "span 3" }}
                      >
                        {t("No results match your search")}
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      title={t("emptyState.noDocument")}
                      description={t("emptyState.hintDocument")}
                      buttonText={
                        canAddDocuments ? t("Add a document") : undefined
                      }
                      onButtonClick={
                        canAddDocuments
                          ? () => setAddModalOpen(true)
                          : undefined
                      }
                      buttonIcon="fa-upload"
                    />
                  )}
                </StandardTable>
              </Card>
            </div>
          )}

          <Modal
            isOpen={addModalOpen}
            toggle={() => setAddModalOpen(false)}
            size="md"
          >
            <ModalHeader toggle={() => setAddModalOpen(false)}>
              {t("Add a document")}
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>{t("Type")}</Label>
                <Input
                  type="select"
                  value={newDoc.type}
                  onChange={(e) =>
                    setNewDoc((prev) => ({ ...prev, type: e.target.value }))
                  }
                >
                  {DOC_TYPES.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>{t("Name")} *</Label>
                <Input
                  type="text"
                  value={newDoc.name}
                  onChange={(e) =>
                    setNewDoc((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder={t("Name")}
                />
              </FormGroup>
              <FormGroup>
                <Label>URL *</Label>
                <Input
                  type="url"
                  value={newDoc.url}
                  onChange={(e) =>
                    setNewDoc((prev) => ({ ...prev, url: e.target.value }))
                  }
                  placeholder="https://..."
                />
              </FormGroup>
              <FormGroup>
                <Label>{t("Date")}</Label>
                <Input
                  type="date"
                  value={newDoc.date}
                  onChange={(e) =>
                    setNewDoc((prev) => ({ ...prev, date: e.target.value }))
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label>{t("Extension")}</Label>
                <Input
                  type="text"
                  value={newDoc.extension}
                  onChange={(e) =>
                    setNewDoc((prev) => ({
                      ...prev,
                      extension: e.target.value,
                    }))
                  }
                  placeholder="pdf"
                />
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={newDoc.sharedWithClient}
                    onChange={(e) =>
                      setNewDoc((prev) => ({
                        ...prev,
                        sharedWithClient: e.target.checked,
                      }))
                    }
                  />
                  {t("Share with client")}
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setAddModalOpen(false)}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddDocument}
                disabled={savingDocument}
              >
                {savingDocument ? t("Saving...") : t("Upload file")}
              </button>
            </ModalFooter>
          </Modal>
        </div>
      </div>
    );
  }
};

export default Document;
